import type { ReactNode } from "react";

/**
 * A deliberately small renderer for the subset our own docs are written in:
 * two heading levels, paragraphs, both list kinds, fenced code, inline code and
 * bold. The input is authored in this repo, so an unsupported construct is a
 * writing mistake rather than something to degrade gracefully around.
 */
type Block =
	| { kind: "heading"; text: string }
	| { kind: "paragraph"; text: string }
	| { kind: "list"; ordered: boolean; items: string[] }
	| { kind: "code"; lines: string[] };

const INLINE = /(`[^`]+`|\*\*[^*]+\*\*)/g;

const inline = (text: string): ReactNode[] =>
	text
		.split(INLINE)
		.filter(Boolean)
		.map((part, index) => {
			if (part.startsWith("`") && part.endsWith("`")) {
				return (
					<code key={index} className="rounded-sm bg-raised px-1 py-0.5 text-[0.85em] text-ink">
						{part.slice(1, -1)}
					</code>
				);
			}
			if (part.startsWith("**") && part.endsWith("**")) {
				return (
					<strong key={index} className="font-medium text-ink">
						{part.slice(2, -2)}
					</strong>
				);
			}
			return part;
		});

const parse = (source: string): Block[] => {
	const blocks: Block[] = [];
	const lines = source.replace(/\t/g, "  ").split("\n");
	let index = 0;

	while (index < lines.length) {
		const line = lines[index] ?? "";

		if (!line.trim()) {
			index += 1;
			continue;
		}

		if (line.startsWith("```")) {
			const code: string[] = [];
			index += 1;
			while (index < lines.length && !(lines[index] ?? "").startsWith("```")) {
				code.push(lines[index] ?? "");
				index += 1;
			}
			index += 1;
			blocks.push({ kind: "code", lines: code });
			continue;
		}

		if (line.startsWith("### ")) {
			blocks.push({ kind: "heading", text: line.slice(4) });
			index += 1;
			continue;
		}

		const ordered = /^\d+\.\s/.test(line);
		if (line.startsWith("- ") || ordered) {
			const items: string[] = [];
			while (index < lines.length) {
				const current = lines[index] ?? "";
				const isOrdered = /^\d+\.\s/.test(current);
				if (!current.startsWith("- ") && !isOrdered) break;
				items.push(current.replace(/^(-\s|\d+\.\s)/, ""));
				index += 1;
			}
			blocks.push({ kind: "list", ordered, items });
			continue;
		}

		const paragraph: string[] = [];
		while (index < lines.length) {
			const current = lines[index] ?? "";
			if (!current.trim() || current.startsWith("### ") || current.startsWith("```")) break;
			if (current.startsWith("- ") || /^\d+\.\s/.test(current)) break;
			paragraph.push(current.trim());
			index += 1;
		}
		blocks.push({ kind: "paragraph", text: paragraph.join(" ") });
	}

	return blocks;
};

export const Markdown = ({ source }: { source: string }) => (
	<div className="space-y-3">
		{parse(source).map((block, index) => {
			if (block.kind === "heading") {
				return (
					<h4 key={index} className="pt-2 text-sm font-medium text-ink">
						{inline(block.text)}
					</h4>
				);
			}

			if (block.kind === "code") {
				return (
					<pre
						key={index}
						className="overflow-x-auto rounded-md border border-line bg-raised px-3 py-2.5 text-xs text-ink"
					>
						<code>{block.lines.join("\n")}</code>
					</pre>
				);
			}

			if (block.kind === "list") {
				const ListTag = block.ordered ? "ol" : "ul";
				return (
					<ListTag
						key={index}
						className={`space-y-1.5 pl-5 text-sm text-ink-muted ${
							block.ordered ? "list-decimal" : "list-disc"
						}`}
					>
						{block.items.map((item, itemIndex) => (
							<li key={itemIndex} className="pl-1">
								{inline(item)}
							</li>
						))}
					</ListTag>
				);
			}

			return (
				<p key={index} className="text-sm leading-relaxed text-ink-muted">
					{inline(block.text)}
				</p>
			);
		})}
	</div>
);
